'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState } from 'react';
import { Rating } from '../../../StarRating/components/Rating';
import { ChartLegend } from './ChartLegend';
import { ChartWrapper } from './ChartWrapper';
import { DEFAULT_COLORS } from './constants';
import { generateHoverSelectors } from './utils';

const PiePlot = dynamic(() => import('@mui/x-charts/PieChart').then(mod => mod.PiePlot), {
  ssr: false,
});

const ChartsSurface = dynamic(
  () => import('@mui/x-charts/ChartsSurface').then(mod => mod.ChartsSurface),
  {
    ssr: false,
  },
);

const ChartDataProvider = dynamic(
  () => import('@mui/x-charts/ChartDataProvider').then(mod => mod.ChartDataProvider),
  {
    ssr: false,
  },
);

export interface Category {
  label: string;
  tooltipLabel: string;
  value: number;
  color?: string;
}

type CategoriesProps = {
  items: Category[];
  overallRating?: number;
};

const CONTAINER_ID = 'star-rating-categories';
const PIE_ARC_SELECTOR = '.MuiPieArc-root';
const LEGEND_DETAILS_SELECTOR = 'details.star-rating-categories-legend-item';

export function Categories({ items, overallRating = 5 }: CategoriesProps) {
  // Ref updates don't re-render; if the effect runs before the node exists, it never retries without state.
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const setContainerRef = useCallback((el: HTMLDivElement | null) => {
    setContainerEl(el);
  }, []);
  const { hoverSelectors, openSelectors, arcHoverSelectors, arcToLegendSelectors } =
    generateHoverSelectors(items.length);

  useEffect(() => {
    const container = containerEl;
    if (!container) return;

    const expectedArcCount = items.length;
    let listenerCleanup: (() => void) | null = null;

    function setupArcListeners() {
      if (!container) return;
      const pieArcs = container.querySelectorAll<HTMLElement>(PIE_ARC_SELECTOR);
      const details = container.querySelectorAll<HTMLDetailsElement>(LEGEND_DETAILS_SELECTOR);

      if (pieArcs.length !== expectedArcCount || details.length === 0) return;

      const cleanups: Array<() => void> = [];
      pieArcs.forEach((arc, index) => {
        const handleInteraction = (e: Event) => {
          e.preventDefault();
          if (
            details.length > 0 &&
            ((e.type === 'mouseenter' && details[index]?.getAttribute('open') === null) ||
              e.type !== 'mouseenter')
          ) {
            details[index]?.toggleAttribute('open');
          }
        };

        const handleTouchStart = (e: Event) => {
          if (e.cancelable) e.preventDefault();
        };

        arc.addEventListener('mouseenter', handleInteraction);
        arc.addEventListener('mouseleave', handleInteraction);
        arc.addEventListener('touchstart', handleTouchStart, { passive: false });

        cleanups.push(() => {
          arc.removeEventListener('mouseenter', handleInteraction);
          arc.removeEventListener('mouseleave', handleInteraction);
          arc.removeEventListener('touchstart', handleTouchStart);
        });
      });

      listenerCleanup = () => {
        cleanups.forEach(fn => fn());
      };
    }

    /** Only react to mutations that add/remove nodes in a subtree that contains pie arcs. */
    function mutationTouchesPieArcs(mutation: MutationRecord): boolean {
      if (mutation.type !== 'childList') return false;
      const subtreeHasArc = (root: Node): boolean => {
        if (root.nodeType !== Node.ELEMENT_NODE) return false;
        const el = root as Element;
        return el.matches(PIE_ARC_SELECTOR) || el.querySelector(PIE_ARC_SELECTOR) != null;
      };
      for (const n of mutation.addedNodes) {
        if (subtreeHasArc(n)) return true;
      }
      for (const n of mutation.removedNodes) {
        if (subtreeHasArc(n)) return true;
      }
      return false;
    }

    let lastPieArcEls: HTMLElement[] | null = null;

    function trySetup() {
      if (!container) return;
      const pieArcs = container.querySelectorAll<HTMLElement>(PIE_ARC_SELECTOR);
      if (pieArcs.length === expectedArcCount) {
        const arcs = Array.from(pieArcs);
        const sameNodes =
          lastPieArcEls !== null &&
          lastPieArcEls.length === arcs.length &&
          lastPieArcEls.every((node, i) => node === arcs[i]);

        // Same DOM nodes and listeners already attached — skip teardown/re-attach churn.
        if (sameNodes && listenerCleanup) return;

        lastPieArcEls = arcs;
        listenerCleanup?.();
        setupArcListeners();
      } else if (pieArcs.length === 0) {
        lastPieArcEls = null;
        listenerCleanup?.();
        listenerCleanup = null;
      } else {
        lastPieArcEls = null;
        listenerCleanup?.();
        listenerCleanup = null;
      }
    }

    trySetup();

    const observer = new MutationObserver(mutations => {
      if (mutations.some(mutationTouchesPieArcs)) trySetup();
    });
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      listenerCleanup?.();
    };
  }, [containerEl, items.length]);

  return (
    <ChartDataProvider
      colors={items.map((item, i) => item.color ?? DEFAULT_COLORS[i])}
      series={[
        {
          data: items,
          type: 'pie',
          innerRadius: 70,
          outerRadius: 140,
          startAngle: 0,
          endAngle: 360,
          cx: '40%',
          cy: '55%',
        },
      ]}
      width={292}
      height={292}
    >
      <ChartWrapper>
        <Box
          ref={setContainerRef}
          id={CONTAINER_ID}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: { xs: 'column-reverse', lg: 'row' },
            gap: '32px',
            // Set default opacity for all pie arcs
            '.MuiPieArc-root': {
              opacity: 1,
            },
            // Enable mobile hover on legend items that are not accordions
            '&:has(div.star-rating-categories-legend-item:hover) .MuiPieArc-root': {
              opacity: 0.2,
            },
            // Dim all arcs when any accordion is open
            '&:has(.star-rating-categories-legend-item[open]) .MuiPieArc-root': { opacity: 0.2 },
            // Desktop-only hover effects
            '@media (min-width: 900px)': {
              '&:has(details.star-rating-categories-legend-item:hover:not([open])) .MuiPieArc-root':
                {
                  opacity: 0.2,
                },
              '&:has(.MuiPieArc-root:hover) .MuiPieArc-root': {
                opacity: 0.2,
              },
              // Highlight specific arc on legend hover (desktop only)
              [hoverSelectors]: { opacity: 1 },
              // Highlight specific arc on pie chart hover (desktop only)
              [arcHoverSelectors]: { opacity: 1 },
            },
            // Highlight specific arc when accordion is open (mobile and desktop)
            [openSelectors]: { opacity: 1 },
            [arcHoverSelectors]: { opacity: 1 },
            [arcToLegendSelectors]: { borderColor: 'var(--legend-hover-color)' },
          }}
        >
          <ChartLegend data={items} />
          <Box sx={{ display: 'flex', justifyContent: 'center', flex: 40 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                width: '292px',
                height: '292px',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2px',
                }}
              >
                <Typography sx={{ fontSize: '24px', color: 'neutral.darkest', fontWeight: 700 }}>
                  {overallRating?.toFixed(1)}
                </Typography>
                <Rating
                  value={overallRating}
                  size="15px"
                  gap="6px"
                  color="#8DC63F"
                  additionalStyles={{ marginRight: '2px' }}
                />
              </Box>
              <ChartsSurface>
                <PiePlot
                  skipAnimation
                  slotProps={{ pieArc: { style: { stroke: 'transparent', strokeWidth: 0 } } }}
                />
              </ChartsSurface>
            </Box>
          </Box>
        </Box>
      </ChartWrapper>
    </ChartDataProvider>
  );
}
